import { Light } from '../light';
import { RenderTexture } from '../renderer/RenderTarget';
import { nextPow2 } from '../math';

export class LightDataTexture {
  readonly texture: RenderTexture;
  count: number = 0;
  capacity: number = 0;

  private data!: Float32Array;

  constructor(private gl: WebGL2RenderingContext) {
    this.texture = new RenderTexture();
    this.texture.glTexture = gl.createTexture()!;
  }

  /**
   * Pack and upload light data. When `shadowSet` is provided, lights in that set
   * get d2.w = 1.0 so the batched deferred shader can skip them (they are
   * rendered individually with shadow maps).
   */
  update(lights: Light[], shadowSet?: Set<Light>): void {
    const gl = this.gl;
    this.count = lights.length;
    if (this.count === 0) return;

    // Grow capacity in powers of 2
    if (this.count > this.capacity) {
      this.capacity = nextPow2(this.count);
      this.data = new Float32Array(this.capacity * 16);

      // Reallocate texture. Raw bind — see TextureState.setTexture in
      // Context.ts for why upload paths don't go through TextureState.
      gl.bindTexture(gl.TEXTURE_2D, this.texture.glTexture!);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, 4, this.capacity, 0, gl.RGBA, gl.FLOAT, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }

    // Pack light data: 4 RGBA texels per light (16 floats per row).
    // Each light subclass writes its own type-specific layout.
    for (let i = 0; i < this.count; i++) {
      lights[i].packGpuData(this.data, i * 16);
      // Shadow flag encoded in d0.w (light type): add 4.0 for shadow-casting
      // lights so the batched deferred shader can skip them with a single
      // comparison (type >= 4.0) without an extra texture fetch.
      if (shadowSet && shadowSet.has(lights[i])) {
        this.data[i * 16 + 3] += 4.0;
      }
    }

    // Upload only the rows we need. Raw bind — see Context.ts policy.
    gl.bindTexture(gl.TEXTURE_2D, this.texture.glTexture!);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      4,
      this.count,
      gl.RGBA,
      gl.FLOAT,
      this.data.subarray(0, this.count * 16),
    );
  }

  destroy(): void {
    if (this.texture.glTexture) {
      this.gl.deleteTexture(this.texture.glTexture);
    }
  }
}

